import React, { useEffect, useMemo, useState } from "react";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";
import {
  Grid,
  Paper,
  TablePagination,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import moment from "moment";
import { LoadingButton } from "@mui/lab";
import { downloadFile } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import { ICity } from "src/types/generic";
import { DatePicker } from "@mui/x-date-pickers";
import { notificationsState } from "src/store/notifications";
import { useSetRecoilState } from "recoil";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";
import NoFilterSelected from "src/shared/components/no-filters-selected";
import {
  useExportGolfCartRequests,
  useGetGolfCartRequests,
} from "src/api/transactions";
import { useGetCitiesList } from "src/api/general";
import StatusChip from "src/shared/components/status-chip";

const tableHeadText = [
  "Request ID",
  "Location",
  "Request Date",
  "Request Time",
  "NO. of Passengers",
  "Requested By",
  "Status",
  "Action By",
  "Action Date",
  "Action Time",
  "Give For Free",
  "Amount Before",
  "Amount After",
];

const minDate = new Date("2024-01-01T00:00:00");
const maxDate = moment().add(1, "year").endOf("year").toDate();

const RequestsTable = () => {
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);
  const setNotifications = useSetRecoilState(notificationsState);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchBy, setSearchBy] = useState<string>("");
  const [tempSearchBy, setTempSearchBy] = useState<string>("");
  const [tempLocation, setTempLocation] = useState<any>(null);
  const [location, setLocation] = useState<any>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [selectedCity, setSelectedCity] = useState<ICity>();
  const [tempSelectedCity, setTempSelectedCity] = useState<ICity>();

  const [page, setPage] = useState(0);

  const fromDate = moment(startDate).startOf("day").valueOf();

  const toDate = moment(endDate).endOf("day").valueOf();

  const filters = {
    locationId: location?.id,
    ...(selectedCity && { cityId: selectedCity.id }),
    ...(startDate && { dateFrom: `${fromDate}` }),
    ...(endDate && { dateTo: `${toDate}` }),
  };

  // APIS
  const { data: locationsData } = useGetLocationsList({ hasGolfCar: true });
  const { data: citiesData } = useGetCitiesList({ type: "active" });
  const { data, isLoading, status, error, refetch } = useGetGolfCartRequests({
    ...filters,
    limit: rowsPerPage,
    page: page + 1,
  });
  const requestsData = data?.data;

  const { refetch: refetchExport, isFetching: isFetchingExport } =
    useExportGolfCartRequests(filters);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeLocation = (event: React.SyntheticEvent, newValue: any) => {
    setTempLocation(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeCity = (event: React.SyntheticEvent, newValue: any) => {
    setTempSelectedCity(newValue);
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate && tempStartDate > tempEndDate) {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: "Start date can't be after end date!",
        },
      ]);
      return;
    }
    setLocation(tempLocation);
    setSearchBy(tempSearchBy);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedCity(tempSelectedCity);
  };

  const handleResetFilers = () => {
    setPage(0);
    setLocation(undefined);
    setSearchBy("");
    setStartDate(null);
    setEndDate(null);
    setSelectedCity(undefined);

    setTempLocation(undefined);
    setTempSearchBy("");
    setTempStartDate(null);
    setTempEndDate(null);
    setTempSelectedCity(undefined);
  };

  const handleExport = () => {
    refetchExport().then((res) => {
      if (res.status === "success" && res.data) {
        const utf8CSVData = `\uFEFF${res.data}`;
        downloadFile(utf8CSVData, "text/csv", "golf-cart-requests");
        setNotifications((prev) => [
          ...prev,
          {
            type: "success",
            message: "Downloaded successfully",
          },
        ]);
      }
    });
  };

  const emptyDataArr = requestsData?.requests?.length === 0;

  useEffect(() => {
    setPage(0);
  }, [location, startDate, endDate, searchBy]);

  useEffect(() => {
    if (requestsData) {
      setTotalCount(requestsData?.total);
    }
  }, [requestsData]);

  useEffect(() => {
    setTempSelectedCity(undefined);
  }, [tempLocation]);

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) ?? []
    );
  }, [locationsData]);

  // error handling
  useEffect(() => {
    if (status === "error") {
      setNotifications((prev) => [
        ...prev,
        {
          type: "error",
          message: error?.data?.message ?? "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  const filteredCities = useMemo(() => {
    if (!citiesData?.data) return [];
    if (!tempLocation) {
      return citiesData.data;
    }
    const availableLocations = tempLocation ? [tempLocation] : sortedLocations;
    const availableLocationsCitiesIds = availableLocations.map(
      (loc: any) => loc.cityId,
    );
    return citiesData.data.filter((city) =>
      availableLocationsCitiesIds.includes(city.id),
    );
  }, [tempLocation, citiesData, sortedLocations]);

  return (
    <>
      <Grid container spacing={1} mb={2}>
        {/* locations */}
        <Grid item xs={12} md={2}>
          <CustomAutocomplete
            size="small"
            fullWidth
            disablePortal
            id="combo-box-demo"
            options={sortedLocations}
            key={tempLocation?.id ?? "loc-autocomplete"}
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            value={tempLocation}
            onChange={handleChangeLocation}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"Location"} />
            )}
          />
        </Grid>

        {/* city */}
        <Grid item xs={12} md={2}>
          <CustomAutocomplete
            key={tempSelectedCity?.id ?? "city-autocomplete"}
            size="small"
            disablePortal
            id="combo-box-demo"
            options={filteredCities}
            getOptionLabel={(option) => option.nameEn}
            getOptionKey={(option) => `${option.id}`}
            value={tempSelectedCity}
            onChange={handleChangeCity}
            renderInput={(params: any) => (
              <TextField fullWidth {...params} label={"City"} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          {/* start date */}
          <DatePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                inputProps: { readOnly: true },
                fullWidth: true,
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          {/* end date */}
          <DatePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                inputProps: { readOnly: true },
                fullWidth: true,
              },
            }}
            format={"dd/MM/yyyy"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          display={"flex"}
          alignItems={"flex-end"}
          direction={"column"}
          gap={2}
        >
          <Stack direction="row" gap={2}>
            <Button onClick={handleResetFilers}>Clear</Button>
            <Button
              variant="contained"
              onClick={handleApply}
              sx={{ width: "100px" }}
            >
              Apply
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Box mb={3}>
        {true ? (
          <Grid
            item
            xs={12}
            md={2}
            display={"flex"}
            justifyContent={"flex-end"}
          >
            <LoadingButton
              variant="contained"
              sx={{ gap: 0.5 }}
              onClick={handleExport}
              loading={isFetchingExport}
            >
              Export
            </LoadingButton>
          </Grid>
        ) : null}
      </Box>
      {false ? (
        <NoFilterSelected />
      ) : (
        <Paper
          sx={{
            bgcolor: "#1A1A1A",
            boxShadow: "0px 14px 44px 0px rgba(0, 0, 0, 0.35)",
            padding: { xs: "15px", md: "30px" },
            width: "100%",
            overflow: "hidden",
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1800,
                "& .MuiTableCell-root": {
                  whiteSpace: "nowrap",
                },
              }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  {tableHeadText.map((item) => (
                    <TableCell
                      key={item}
                      sx={{
                        fontWeight: 600,
                        color: "#A1A5B7",
                      }}
                    >
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton
                  rowsLength={rowsPerPage}
                  cellsLength={13}
                />
              ) : (
                <TableBody>
                  {requestsData?.requests?.map((row: any) => {
                    const actionTime = row.canceledAt || row.acceptedAt;
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          cursor: "pointer",
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.locationName}</TableCell>
                        <TableCell>
                          {moment(row.requestTime).format("YYYY-MM-DD")}
                        </TableCell>
                        <TableCell>
                          {moment(row.requestTime).format("hh:mm A")}
                        </TableCell>
                        <TableCell>{row.passengers}</TableCell>
                        <TableCell>{row.requestedByName}</TableCell>
                        <TableCell>
                          <StatusChip
                            color={
                              row.status === "pending"
                                ? "warning"
                                : row.status === "accepted"
                                  ? "success"
                                  : "error"
                            }
                            label={row.status}
                          />
                        </TableCell>
                        <TableCell>
                          {row.canceledByName
                            ? `${row.canceledByName}`
                            : row.acceptedByName
                              ? `${row.acceptedByName}`
                              : ""}
                        </TableCell>
                        <TableCell>
                          {actionTime
                            ? moment(actionTime).format("DD/MM/YYYY")
                            : ""}
                        </TableCell>
                        <TableCell>
                          {actionTime
                            ? moment(actionTime).format("hh:mm A")
                            : ""}
                        </TableCell>
                        <TableCell>{row.isFreeTicket ? "Yes" : "No"}</TableCell>
                        <TableCell>{row.amountBefore}</TableCell>
                        <TableCell>{row.amountAfter}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>

            {emptyDataArr && <NoData />}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              disabled={isLoading}
            />
          </TableContainer>
        </Paper>
      )}
    </>
  );
};
export default RequestsTable;
